import { Link, useSearchParams } from 'react-router-dom'
import { ChartColumn, Hourglass, Workflow } from 'lucide-react'
import type { CompanyLeadStatsDto } from '@lib/dto/lead-stats-numbers.dto'
import { leadStatsViewSchema } from '@lib/enums/lead-stats-view.enum'
import type { LeadStatsQuery } from '@lib/input/lead-stats.input'
import { calendarDaySchema } from '@lib/validators/temporal.validator'
import { Badge } from '@/components/ui/badge'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { FilterChip, FilterOption } from '@/components/ui/filter-chip'
import { DetailSkeleton, EmptyState, ErrorState } from '@/components/ui/states'
import { Table, TableBody, TableCell, TableEmptyRow, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { periodHeading } from '@/components/ads/CompanyAdsTable'
import { PeriodBar, ViewToggle } from '@/components/stats/StatsPieces'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useOrganization } from '@/hooks/useOrganization'
import { useCompanyLeadStats } from '@/hooks/useLeadStats'
import { formatCount } from '@/lib/adFormat'

/** A conversion to one decimal: `14.3%`. */
function percent(fraction: number): string {
  return new Intl.NumberFormat(undefined, { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(fraction)
}

function cell(kind: CompanyLeadStatsDto['columns'][number]['kind'], value: number | null): string {
  if (value === null) return '—'
  return kind === 'conversion' ? percent(value) : formatCount(value)
}

const STATE_LABEL: Record<Exclude<CompanyLeadStatsDto['rows'][number]['state'], 'ready'>, string> = {
  first_read: 'Reading for the first time',
  not_working: 'GoHighLevel stopped working',
}

/**
 * Every venue's lead stats for one period (docs/gohighlevel-stats.spec.md, "The
 * company table"). Organization admins only; each venue links to its dashboard.
 */
export function LeadsPage() {
  const organizationQuery = useOrganization()
  const hasGhl = organizationQuery.data?.features.includes('gohighlevel') ?? false
  const [params, setParams] = useSearchParams()
  const view = leadStatsViewSchema.safeParse(params.get('view'))
  const period = calendarDaySchema.safeParse(params.get('period'))
  const pipeline = params.get('pipeline')
  const query: LeadStatsQuery = {
    ...(pipeline === null ? {} : { pipeline }),
    ...(view.success ? { view: view.data } : {}),
    ...(period.success ? { period: period.data } : {}),
  }
  const statsQuery = useCompanyLeadStats(query, hasGhl)

  function set(key: 'pipeline' | 'view' | 'period', value: string): void {
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        next.set(key, value)
        return next
      },
      { replace: true },
    )
  }

  if (organizationQuery.isLoading || (hasGhl && statsQuery.isPending)) {
    return (
      <PageContainer width="wide">
        <DetailSkeleton />
      </PageContainer>
    )
  }
  if (!hasGhl) {
    return (
      <PageContainer width="wide">
        <EmptyState icon={ChartColumn} title="GoHighLevel isn’t turned on" description="Ask CSE to turn it on for your company." />
      </PageContainer>
    )
  }
  if (statsQuery.isError) {
    return (
      <PageContainer width="wide">
        <ErrorState message={requestErrorMessage(statsQuery.error)} onRetry={() => void statsQuery.refetch()} />
      </PageContainer>
    )
  }
  const data = statsQuery.data
  if (data === undefined) return null

  const header = <PageHeader title="Leads" meta={organizationQuery.data?.name} />
  if (data.pipelines.length === 0) {
    return (
      <PageContainer width="wide" className="space-y-6">
        {header}
        <p className="m-0 text-sm text-ink-muted">
          No numbers are defined yet.{' '}
          <Link to="/gohighlevel" className="font-medium text-accent-ink hover:underline">
            Define them on the GoHighLevel page
          </Link>
          .
        </p>
      </PageContainer>
    )
  }

  const pipelineName = data.pipelines.find((p) => p.id === data.pipelineId)?.name ?? null

  return (
    <PageContainer width="wide" className="space-y-6">
      {header}
      <div className="flex flex-wrap items-center gap-3">
        <ViewToggle view={data.view} onView={(v) => set('view', v)} />
        {data.pipelines.length > 1 ? (
          <FilterChip property="Pipeline" icon={Workflow} valueLabel={pipelineName}>
            {(close) =>
              data.pipelines.map((p) => (
                <FilterOption
                  key={p.id}
                  selected={p.id === data.pipelineId}
                  onSelect={() => {
                    set('pipeline', p.id)
                    close()
                  }}
                >
                  {p.name}
                </FilterOption>
              ))
            }
          </FilterChip>
        ) : null}
      </div>
      {data.window === null ? null : (
        <PeriodBar window={data.window} periods={data.periods} spend={null} onPeriod={(start) => set('period', start)} />
      )}
      <div className="space-y-2">
        <Table heading={data.window === null ? 'No numbers yet' : periodHeading(data.window)}>
          <TableHeader>
            <TableHead>Venue</TableHead>
            {data.columns.map((c) => (
              <TableHead key={c.metricId} className="text-right">
                {c.name}
              </TableHead>
            ))}
          </TableHeader>
          <TableBody>
            {data.rows.length === 0 ? (
              <TableEmptyRow colSpan={data.columns.length + 1}>No venue has a GoHighLevel connection yet.</TableEmptyRow>
            ) : null}
            {data.rows.map((row) => (
              <TableRow key={row.venueId}>
                <TableCell className="min-w-40">
                  <Link to={`/venues/${row.venueId}`} className="font-medium text-ink underline-offset-2 hover:underline">
                    {row.venueName}
                  </Link>
                  {row.stillMoving ? (
                    <span className="ml-1.5 inline-flex align-middle text-ink-subtle" aria-label="Still moving">
                      <Hourglass className="h-3 w-3" aria-hidden="true" />
                    </span>
                  ) : null}
                  {row.state === 'ready' ? null : (
                    <div>
                      <Badge tone="warning">{STATE_LABEL[row.state]}</Badge>
                    </div>
                  )}
                </TableCell>
                {data.columns.map((c) => (
                  <TableCell key={c.metricId} className="text-right font-mono tabular-nums">
                    {cell(c.kind, row.values.find((v) => v.metricId === c.metricId)?.value ?? null)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {data.rows.length > 1 ? (
              <TableRow className="font-medium">
                <TableCell>All venues</TableCell>
                {data.columns.map((c) => (
                  <TableCell key={c.metricId} className="text-right font-mono tabular-nums">
                    {cell(c.kind, data.totals.find((t) => t.metricId === c.metricId)?.value ?? null)}
                  </TableCell>
                ))}
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
        <p className="m-0 text-xs text-ink-muted">
          Couples are added up across venues, and conversions worked out from those totals. An hourglass marks a venue whose couples are
          still touring and booking.
        </p>
      </div>
    </PageContainer>
  )
}
